'use client';
import { FC, JSX, useEffect, useState } from 'react';

import Modal, { ModalPropTypes } from '@/layouts/Modal';
import { getNearbyStores } from '@/app/actions';
import { storage } from '@/utils/storage';
import { Svg, Text } from '@/components';
import { ChevronRightIcon } from '@/icons';
import { useLocation } from '@/context/locationContext';
import {
  DEFAULT_STOCK_ID,
  DEFAULT_STORE_CODE,
  TOKEN_EXPIRY_TIME,
} from '@/constants';
import { useCart } from '@/context/cartContext';
import { useMakeYourGiftStore } from '@/store/make-your-gift-store';
import { setCookie } from 'cookies-next';
import { pushGA4Event } from '@/utils/helper';

type PropTypes = ModalPropTypes & {
  redirectTo?: string;
  asGuest?: boolean;
  message?: string;
};

const PickupStore: FC<PropTypes> = ({ show, onClose }): JSX.Element => {
  const {
    setShowStoreChangeModal,
    setSelectedStore,
    setDeliveryType,
    setIsDelivery,
    setLocationToast,
    setHeaderLocationTitle,
    setStoreCode,
    handleStoreChange,
    defaultLat,
    defaultLng,
  } = useLocation();
  const { cartItems } = useCart();
  const [stores, setStores] = useState<any[]>([]);
  const currentStoreCode = storage.get('store_code') || '';
  const { removeBox } = useMakeYourGiftStore();
  useEffect(() => {
    const fetchStores = async () => {
      const data = await getNearbyStores(
        defaultLat ?? undefined,
        defaultLng ?? undefined
      );
      if (data?.store) {
        // Sort stores to show current store first
        const sortedStores = [...data.store].sort((a, b) => {
          if (a.code === currentStoreCode) return -1;
          if (b.code === currentStoreCode) return 1;
          return 0;
        });
        setSelectedStore(sortedStores[0]);
        setStores(sortedStores);
      }
    };
    fetchStores();
  }, [currentStoreCode]);

  const handleStoreSelect = async (store: any) => {
    // GA4 Discovery: store_locator_search
    pushGA4Event('store_locator_search', {
      city: store?.city,
      store_name: store?.name,
    });
    setSelectedStore(store);
    if (currentStoreCode !== store.code && cartItems && cartItems.length > 0) {
      setIsDelivery(false);
      storage.set('isDelivery', 'false');
      setShowStoreChangeModal(true);
      onClose();
      return;
    }
    storage.set('deliveryType', 'pickup');
    setDeliveryType('pickup');
    storage.set('address', store.name);
    setHeaderLocationTitle(store.name);
    storage.set('storeTitle', store.name);
    storage.set('storeDescription', `${store.street[0]} ${store.street[1]}`);
    storage.set('store_code', store?.code || DEFAULT_STORE_CODE);
    setStoreCode(store?.code || DEFAULT_STORE_CODE);
    storage.set('stock_id', store?.stock_id || DEFAULT_STOCK_ID);
    storage.set('wizzy_api_key', store?.wizzy_api_key);
    storage.set('wizzy_store_id', store?.wizzy_store_id);
    const cookiesToSet = [
      { key: 'store_code', value: store.code },
      { key: 'stock_id', value: store?.stock_id || DEFAULT_STOCK_ID },
      { key: 'wizzy_api_key', value: store?.wizzy_api_key },
      { key: 'wizzy_store_id', value: store?.wizzy_store_id },
    ];
    if (store?.min_order_value_enable === '1') {
      const movData = {
        message: store?.min_order_value_message,
        amount: {
          home: store?.min_order_value,
          pickup: store?.min_order_value_store_pickup,
        },
      };
      cookiesToSet.push({
        key: 'mov',
        value: JSON.stringify(movData),
      });
    }
    for (const cookie of cookiesToSet) {
      setCookie(cookie.key, cookie.value, {
        maxAge: TOKEN_EXPIRY_TIME,
      });
    }
    if (currentStoreCode !== store.code) {
      await handleStoreChange({
        stock_id: store?.stock_id,
        store_code: store?.code,
        default_store_code: store?.default_store_code,
        wizzy_api_key: store?.wizzy_api_key,
        wizzy_store_id: store?.wizzy_store_id,
      });
      removeBox();
    }
    if (currentStoreCode !== store.code) {
      storage.set('show_location_toast', 'true');
      window.location.reload();
    } else {
      setLocationToast({
        message: 'Your Pickup Store has been changed.',
        deliveryType: 'pickup',
      });
      setTimeout(() => setLocationToast(undefined), 2000);
    }
    onClose();
  };

  const onPickupStoreClose = () => {
    onClose(); // Close modal
  };

  return (
    <Modal
      show={show}
      onClose={onPickupStoreClose}
      id='pickup-store'
      closeOnOutsideClick={false}
      heightClassName='max-md:h-full max-md:max-h-screen'
      style='max-md:h-full max-md:max-h-screen'>
      <div className='relative flex h-full w-full flex-col max-lg:pb-0'>
        {/* Header */}
        <div aria-label='modal-header' className='relative bg-white p-4'>
          <div className='text-left text-xl font-bold md:text-2xl'>
            Select Store
          </div>
        </div>

        {/* Description */}
        <div className='w-full px-4 text-left text-sm text-[#7f7e81]'>
          Find your preferred store to learn about its opening hours, stock
          availability, and relevant offers
        </div>

        {/* Stores List */}
        {stores.length > 0 ? (
          <div className='mt-4 bg-[#EFECEB4D]'>
            <div className='flex-1 space-y-3 overflow-y-auto px-4  py-6'>
              {stores.map(store => (
                <div
                  key={store.id}
                  onClick={() => handleStoreSelect(store)}
                  className='flex cursor-pointer items-center justify-between  rounded-lg border border-[#ebebed] bg-white p-2 px-3 text-left transition-all hover:border hover:border-[#A0383F]'>
                  <div className='flex flex-col gap-[2px] pr-7'>
                    <div className='flex w-full flex-row items-center justify-between'>
                      <Text className='px-0 text-sm font-semibold'>
                        {store.name}
                      </Text>
                    </div>
                    <Text className='px-0 text-[10px] text-[#38373A]'>
                      {store.street_line1}
                      {store.street_line2 && `, ${store.street_line2}`},{' '}
                      {store.city} {store.postcode}
                    </Text>
                    {/* <Text className='px-0 text-[10px] text-[#908f92]'>
                      {parseInt(store.distance)} km away
                    </Text> */}
                  </div>
                  <Svg className='h-6 w-6 stroke-[#A0383F]'>
                    <ChevronRightIcon />
                  </Svg>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className='base-loading w-full h-40 rounded-lg mt-4' />
        )}
      </div>
    </Modal>
  );
};

export default PickupStore;
